import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '@/components/ToastProvider';
import { autofillArtistByInput, updateArtist } from '@/features/artists/services/artists.api';
import type { Artist } from '@/features/artists/types';

interface Props {
  open: boolean;
  onClose: () => void;
  artist: Artist;
  onSaved: (a: Artist) => Promise<void> | void;
}

const ArtistEditDialog: React.FC<Props> = ({ open, onClose, artist, onSaved }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(artist.name);
  const [photoUrl, setPhotoUrl] = useState(artist.photoUrl || '');
  const [followers, setFollowers] = useState<number>(artist.followers || 0);
  const [genres, setGenres] = useState<string>((artist.genres || []).join(', '));
  const [spotifyId, setSpotifyId] = useState<string>((artist as any).spotifyId || '');
  const [nationality, setNationality] = useState<string>((artist as any).nationality || '');
  const [careerStage, setCareerStage] = useState<string>((artist as any).careerStage || '');
  const [mainGenre, setMainGenre] = useState<string>((artist as any).mainGenre || '');
  const [about, setAbout] = useState<string>((artist as any).about || '');
  const chips = useMemo(() => genres.split(',').map(s => s.trim()).filter(Boolean), [genres]);

  useEffect(() => {
    if (open) {
      setLoading(false); setError(null);
      setName(artist.name);
      setPhotoUrl(artist.photoUrl || '');
      setFollowers(artist.followers || 0);
      setGenres((artist.genres || []).join(', '));
      setSpotifyId((artist as any).spotifyId || '');
      setNationality((artist as any).nationality || '');
      setCareerStage((artist as any).careerStage || '');
      setMainGenre((artist as any).mainGenre || '');
      setAbout((artist as any).about || '');
    }
  }, [open, artist]);

  const handleAutofill = async () => {
    if (!spotifyId) return;
    try {
      setLoading(true); setError(null);
      const data = await autofillArtistByInput(spotifyId);
      setName(data.name);
      setPhotoUrl(data.photoUrl || '');
      setFollowers(data.followers || 0);
      setGenres((data.genres || []).join(', '));
      toast.success('Dados atualizados a partir do Spotify');
    } catch (e:any) {
      const msg = e?.message || 'Falha ao buscar no Spotify';
      setError(msg);
      toast.error('Erro no Spotify', msg);
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      setLoading(true); setError(null);
      const payload: any = {
        name: name.trim(),
        photoUrl: photoUrl.trim() || undefined,
        followers: Number(followers) || 0,
        genres: chips,
        spotifyId: spotifyId || undefined,
        nationality: nationality.trim() || undefined,
        careerStage: careerStage.trim() || undefined,
        mainGenre: mainGenre.trim() || undefined,
        about: about.trim() || undefined,
      };
      const updated = await updateArtist(artist.id, payload) as any;
      await onSaved(updated);
      toast.success('Artista atualizado');
      onClose();
    } catch (e:any) {
      const msg = e?.message || 'Falha ao salvar artista';
      setError(msg);
      toast.error('Erro ao salvar', msg);
    } finally { setLoading(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-xl sm:rounded-2xl bg-white dark:bg-zinc-900 border border-default sm:p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <div className="flex items-center gap-2">
            <i className="ph ph-user-gear text-xl" />
            <h2 className="text-lg font-bold">Editar artista</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200 p-1 rounded-md"><i className="ph ph-x text-xl" /></button>
        </div>

        <div className="px-4 py-4 space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-subtle border border-default">
            <img src={photoUrl || 'https://placehold.co/64x64?text=A'} alt={name} className="w-16 h-16 rounded-md object-cover border border-default" />
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{name || '(sem nome)'}</p>
              <p className="text-xs text-muted">{(followers||0).toLocaleString()} seguidores</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {chips.slice(0,6).map((g,i)=> (<span key={i} className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 text-xs text-muted border border-default">{g}</span>))}
              </div>
            </div>
            <button onClick={handleAutofill} disabled={!spotifyId || loading} className="px-3 py-2 rounded-md bg-zinc-900 text-white dark:bg-green-500 dark:text-black disabled:opacity-50">Atualizar do Spotify</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm text-muted">Nome</span>
              <input value={name} onChange={e=>setName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">Foto (URL)</span>
              <input value={photoUrl} onChange={e=>setPhotoUrl(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">Seguidores</span>
              <input type="number" value={followers} onChange={e=>setFollowers(Number(e.target.value)||0)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">Gêneros (vírgula)</span>
              <input value={genres} onChange={e=>setGenres(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">Spotify ID</span>
              <input value={spotifyId} onChange={e=>setSpotifyId(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">Nacionalidade</span>
              <input value={nationality} onChange={e=>setNationality(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">Estágio de carreira</span>
              <input value={careerStage} onChange={e=>setCareerStage(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">Gênero principal</span>
              <input value={mainGenre} onChange={e=>setMainGenre(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
            </label>
            <label className="block md:col-span-2">
              <span className="text-sm text-muted">Sobre</span>
              <textarea value={about} onChange={e=>setAbout(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent min-h-[80px]" />
            </label>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 rounded-md border border-default">Cancelar</button>
            <button onClick={handleSave} disabled={loading || !name.trim()} className="px-3 py-2 rounded-md bg-green-600 text-white disabled:opacity-50">Salvar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistEditDialog;
